import { useI18n } from '@change-corgi/core/react/i18n';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useCountryCode } from 'src/app/shared/hooks/l10n';
import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useSession } from 'src/app/shared/hooks/session';
import type { PaymentMethod, PaymentType as TrackingPaymentMethod } from 'src/app/shared/utils/payments';
import { calculateImpressions } from 'src/app/shared/utils/promote';

import { usePromoteFormData } from '../../../formContext/formData';
import { usePromoteFormState } from '../../../formContext/formState';

import type { StripeGatewayData } from './api';
import { createPromotionCharge } from './api';
import { useLineItemDescription } from './useLineItemDescription';

export function useCreatePromotion(): (
	paymentMethod: PaymentMethod,
	trackingPaymentMethod: TrackingPaymentMethod,
) => Promise<StripeGatewayData | undefined> {
	const utilityContext = useUtilityContext();
	const { amountToBaseUnits } = useI18n();
	const [{ amount, isPublic }] = usePromoteFormState();
	// Note: email comes from formData.user.email since formState doesn't have email
	const { currency, petition, promotionSettings, user } = usePromoteFormData();
	const countryCode = useCountryCode();
	const session = useSession();
	const sourceLocation = useQueryParamValue('source_location');
	const currentSource = useQueryParamValue('utm_source') || session.trackingData.current_source;
	const lineItemDescription = useLineItemDescription();

	return async function create(paymentMethod, trackingPaymentMethod) {
		const result = await createPromotionCharge(
			{
				amount: amountToBaseUnits(amount, currency.code),
				currencyCode: currency.code,
				customer: {
					countryCode,
					email: user.email,
				},
				paymentMethod,
				productType: 'promotion',
				lineItemDescription: lineItemDescription(trackingPaymentMethod),
			},
			{
				impressionsRequested: calculateImpressions(
					amountToBaseUnits(amount, currency.code),
					promotionSettings.cpm,
					promotionSettings.bonusCpm,
					promotionSettings.bonusThreshold,
				),
				petitionId: petition.id,
				validationToken: promotionSettings.promotionToken,
				visibility: isPublic ? 'PUBLIC' : 'PRIVATE',
				currentSource,
				sourceLocation,
			},
			utilityContext,
		);
		return result;
	};
}
